import React, { useCallback, useMemo, useRef, useState } from 'react';
import styled from 'styled-components/macro';
import { Statuses, StatusI } from 'store/models/Statuses';
import { ButtonGroup, Button, Divider } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import {
  numTicketsActions,
  workdeskFilterActions,
} from 'store/actions';
import { useSelector } from 'react-redux';
import { workdeskFilterSelector } from 'store/selectors';
import { numTicketsStateSelector } from 'store/selectors';
import { useEffect } from 'react';
import {
  ExpandLessRounded,
  ExpandMoreRounded,
} from '@material-ui/icons';
import { TicketType } from 'store/models/TicketTypeHelper';
import { statusCountForTicketTypeSelector } from 'store/selectors/workdesk-filter.selector';
import { isUserAdminSelector } from 'store/selectors/user.selector';

const StatusNav: React.FC = () => {
  const { isLoading } = useSelector(numTicketsStateSelector);
  // new version
  const numTicket = useSelector(statusCountForTicketTypeSelector);
  const isUserAdmin = useSelector(isUserAdminSelector);

  const { selectedTicketType, selectedStatus } = useSelector(
    workdeskFilterSelector,
  );

  // will always collapsed onload (not store in local storage) but if has its value selected as selected status (which is stored in the local storage) will auto expand
  const [
    showPreSIReadyStatuses,
    setShowPreSIReadyStatuses,
  ] = useState(false);

  const hintSelected = useMemo(
    () =>
      !showPreSIReadyStatuses &&
      Statuses.isInHiddenStatuses(selectedStatus),
    [showPreSIReadyStatuses, selectedStatus],
  );
  const dispatch = useDispatch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableDispatch = useCallback(dispatch, []);
  /**
   * call get num tickets when load the page (note when back from main page will load too)
   */
  useEffect(() => {
    stableDispatch(numTicketsActions.getNumTickets());
  }, [stableDispatch]);
  console.debug('numTicket : and isLoading :', numTicket, isLoading);

  const handleSelectTicketType = (typeValue: TicketType) => {
    if (!Statuses.isValidTicketType(typeValue))
      throw Error(`invalid status type`);
    dispatch(workdeskFilterActions.selectTicketType(typeValue));
  };

  const handleToggleShowPreSIReady = () => {
    const newValue = !showPreSIReadyStatuses;
    setShowPreSIReadyStatuses(newValue);
  };
  const handleToggleStatus = useCallback(
    (status: StatusI) => {
      stableDispatch(
        workdeskFilterActions.toggleSelectedStatus(status),
      );
    },
    [stableDispatch],
  );

  /**
   * when non si is selected -> auto toggle to the valid status
   * even onload not just onClick non si button
   * only if user is not admin
   */
  useEffect(() => {
    if (!isUserAdmin && selectedTicketType === 'NON_SI') {
      if (Statuses.isInNonStatuses(selectedStatus)) {
        handleToggleStatus(
          Statuses.getNewSelectedStatusWhenClickNon(selectedStatus),
        );
      }
    }
  }, [
    isUserAdmin,
    selectedTicketType,
    selectedStatus,
    handleToggleStatus,
  ]);

  /* hasCheckedSelectedStatusIfHiddenOnce use with auto toggle show pre si ready */
  const hasCheckedSelectedStatusIfHiddenOnce = useRef(false);

  /**
   * auto toggle show pre si ready if is non when first selectedStaus changed
   */
  useEffect(() => {
    if (
      !hasCheckedSelectedStatusIfHiddenOnce.current &&
      Statuses.isInHiddenStatuses(selectedStatus) &&
      !showPreSIReadyStatuses
    ) {
      setShowPreSIReadyStatuses(true);
    }
    hasCheckedSelectedStatusIfHiddenOnce.current = true;
  }, [selectedStatus, showPreSIReadyStatuses]);

  const renderStatus = (status) => {
    // allow to click if user is admin
    const disabled =
      !isUserAdmin &&
      selectedTicketType === 'NON_SI' &&
      Statuses.isInNonStatuses(status.value);
    return (
      <SStatusItem
        key={status.value}
        className={` ${
          selectedStatus === status.value ? 'selected' : ''
        } ${disabled ? 'disabled' : 'clickable'}`}
        onClick={() =>
          disabled ? null : handleToggleStatus(status.value)
        }
      >
        <SIconWrapper>
          <status.IconClass />
        </SIconWrapper>
        <SText>{status.label}</SText>

        {/* comment out num ticket for now */}
        <SNumOfTicket>
          {numTicket && numTicket[status.value]}
        </SNumOfTicket>
        <SHighlightSelected
          selected={selectedStatus === status.value}
        />
      </SStatusItem>
    );
  };

  const disabledNonTicketType = useMemo(
    () => Statuses.isInNonStatuses(selectedStatus),
    [selectedStatus],
  );

  return (
    <SStatusNav>
      <SButtonGroup color="primary" size="small">
        {Statuses.ticketTypesForNav.map((sType) => {
          //  disable toggle between non-si and si group if user is not admin
          const disabled =
            !isUserAdmin &&
            sType.value === 'NON_SI' &&
            disabledNonTicketType;
          return (
            <STypeButton
              key={sType.value}
              disabled={disabled}
              variant={
                sType.value === selectedTicketType
                  ? 'contained'
                  : 'outlined'
              }
              onClick={() => handleSelectTicketType(sType.value)}
            >
              {sType.label}
            </STypeButton>
          );
        })}
      </SButtonGroup>
      <SStatuses>
        <SShowHiddenButton
          className={hintSelected ? 'hint-selected' : ''}
          fullWidth
          onClick={() => handleToggleShowPreSIReady()}
          startIcon={
            showPreSIReadyStatuses ? (
              <ExpandLessRounded />
            ) : (
              <ExpandMoreRounded />
            )
          }
        >
          {`${showPreSIReadyStatuses ? 'Hide' : 'Show'} Pre SI Ready`}
        </SShowHiddenButton>
        <Divider />
        <SHiddenWrapper
          className={showPreSIReadyStatuses ? '' : 'hide'}
        >
          {Statuses.hiddenStatusesForStatusNav(
            selectedTicketType,
          ).map((status) => {
            return renderStatus(status);
          })}
        </SHiddenWrapper>
        {showPreSIReadyStatuses && <Divider />}
        <SMainWrapper>
          {Statuses.mainStatusesForStatusNav(selectedTicketType).map(
            (status) => {
              return renderStatus(status);
            },
          )}
        </SMainWrapper>
      </SStatuses>
      {/* material style */}
      {/* <List>
        {Statuses.mainStatusesForStatusNav.map((status) => (
          <ListItem button>
            <ListItemIcon>{<status.IconClass />}</ListItemIcon>
            <ListItemText>{status.label}</ListItemText>
          </ListItem>
        ))}
      </List> */}
    </SStatusNav>
  );
};

export default StatusNav;

const SNumOfTicket = styled.div`
  color: ${(props) => props.theme.palette.grey[600]};
  margin-right: 1.5rem;
`;
const SHighlightSelected = styled.div`
  background: ${(props: { selected: boolean; theme: any }) =>
    props.selected && props.theme.palette.primary.main};
  width: 5px;
  height: 63px;
  position: absolute;
  right: 0;
`;
const SStatusItem = styled.div.attrs((props) => ({
  className: props.className,
}))`
  height: 63px;
  position: relative;
  display: flex;
  align-items: center;
  height: 63px;
  &:not(.disabled):hover {
    background: ${(props) => props.theme.palette.grey[300]};
  }
  &.selected {
    background: ${(props) => props.theme.palette.grey[300]};
  }
  &.disabled {
    color: ${(props) => props.theme.palette.text.disabled};
  }
`;
const SStatusNav = styled.div`
  height: 100%;
  width: 230px;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.2);
  padding-top: 0.5rem;
  & ${SStatusItem} + ${SStatusItem}::before {
    position: absolute;
    content: '';
    height: 20px;
    background-color: ${(props) => props.theme.palette.grey[300]};
    width: 3px;
    top: -14px;
    margin-left: 30px;
  }
  background-color: ${(props) =>
    props.theme.palette.background.paper};
`;
const SText = styled.div`
  padding-left: 0.2rem;
  flex: 1;
  font-size: 1rem;
`;
const SIconWrapper = styled.div`
  padding: 0.7rem 1rem;
  & .MuiSvgIcon-root {
    font-size: 2rem;
  }
`;

const SButtonGroup = styled(ButtonGroup)`
  margin: 0.5rem 0;
  width: 100%;
  justify-content: center;
`;
const STypeButton = styled(Button)`
  width: 65px;
  height: 40px;
  font-size: 12px;
`;
const SShowHiddenButton = styled(Button).attrs((props) => ({
  className: props.className,
}))`
  height: 50px;

  &.MuiButton-root {
    margin-top: 0.5rem;
    background: ${(props) => props.theme.palette.grey[100]};
    display: flex;
    justify-content: flex-start;
    padding-left: 1rem;
    box-shadow: 0px 1px 8px 1px #80808033;
    &.hint-selected {
      box-shadow: 0px 2px 10px 7px #b73f8345;
    }
  }
`;
const SHiddenWrapper = styled.div`
  overflow: hidden;

  &.hide {
    height: 0;
    transition: height 0.3s ease-in;
    margin-bottom: 0px;
  }
  height: calc(63px * 2);
  transition: height 0.3s ease-out;
  /* margin-bottom: 10px; */
  background: ${(props) => props.theme.palette.background.paper};
  border-bottom: solid 1px ${(props) => props.theme.palette.grey[100]};
`;
const SMainWrapper = styled.div`
  box-shadow: 0px -1px 10px 1px #80808033;
  background: ${(props) => props.theme.palette.background.paper};
`;
const SStatuses = styled.div`
  /* background: ${(props) => props.theme.palette.grey[50]}; */
`;
